<template>
  <div class="settings-engineers-security-group-detail">
    <div>
      <a
        id="settings-engineers-security-group-detail-back"
        class="nav-back"
        @click="$router.go(-1)"
      >
        {{ $t('back') }}
      </a>
    </div>
    <hr>
    <div class="detail">
      <label class="emphasis">{{ groupDetail.name === AdminRoleName[0] ? $t('settings.security.systemAdmin') : groupDetail.name }}</label>
      <div v-if="allowChangeDescription">
        <input
          id="settings-engineers-security-group-detail-name"
          v-model="groupDescription"
          type="text"
          :class="{ 'red': highlight }"
          @focusout="updateGroupDescription()"
        >
      </div>
      <div v-else>
        <pre @dblclick="dblClickEdit()"><span>{{ groupDescription }}</span></pre>
        <a
          v-if="aclHelper.getSettingsUpdateAccess()"
          id="settings-engineers-security-group-detail-name-edit"
          class="fa fa-pencil icon"
          @click="allowChangeDescription = true"
        />
      </div>
    </div>

    <div class="users">
      <table>
        <thead>
          <th>{{ userHeaders[0].name }} ({{ filteredItems.length }})</th>
          <th>{{ userHeaders[1].name }}</th>
        </thead>
        <tbody>
          <tr v-if="userItems.length > 0">
            <td>
              <input
                id="settings-engineers-security-group-detail-user-filter"
                v-model="userHeaders[0].filter"
                type="text"
              >
            </td>
            <td class="settings-filter">
              <HKLSelect
                :value="userHeaders[1].filter"
                class="align-center"
                settings-filter-row
                :options="userStatusHklOptions"
                :showSearch="false"
                placeholder=""
                @change="val => { userHeaders[1].filter = val }"
              />
            </td>
          </tr>
          <tr
            v-for="(item, index) in filteredItems"
            :key="index"
          >
            <td>
              <a
                :id="'settings-engineers-security-group-detail-user-detail-' + index"
                @click="navToUserDetail(item.id)"
              >
                {{ userName(item) }}
              </a>
            </td>
            <td>
              <span class="status">{{ item.enabled | showStatusLabel }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import HttpStatus from 'http-status-codes'
import _ from 'lodash'

import HKLSelect from '@/controls/Select/HKLSelect.vue'

import { aclHelper } from '@/utils/aclHelper'
import HTTP from '@/utils/http'
import notification from '@/utils/notification'
import userHelper from '@/utils/userHelper'

import { AdminRoleName } from '@/utils/consts'
import { userStatusMapping, userStatusAllOptions } from '@/utils/locale'

export default {
  name: 'SecurityGroupDetail',
  components: {
    HKLSelect,
  },

  data () {
    const userHeaders = [
      {
        code: 'username',
        name: this.$i18n.t('settings.groupUsers'),
        filter: '',
      },
      {
        code: 'status',
        name: this.$i18n.t('status.name'),
        filter: userStatusAllOptions[0],
      },
    ]

    return {
      AdminRoleName,
      roleId: '',
      allowChangeDescription: false,
      invalidName: false,
      groupDescription: '',
      groupDetail: {},
      userHeaders,
      userItems: [],
      removedUsers: [],
      tempRemovedUsers: [],
      userStatusAllOptions,
      aclHelper,
    }
  },

  computed: {
    userStatusHklOptions () {
      return this.userStatusAllOptions.map((value) => ({
        name: this.$options.filters.customStatusLabel(value),
        value,
      }))
    },
    filteredItems () {
      const result = _.filter(this.userItems, item => (
        (item.username.toString().split('@')[0].toLowerCase().indexOf(this.userHeaders[0].filter) !== -1 ||
        (item.displayName || ' ').toString().toLowerCase().indexOf(this.userHeaders[0].filter) !== -1) &&
        (this.userHeaders[1].filter === this.userStatusAllOptions[0] ||
          (this.userHeaders[1].filter === userStatusMapping[item.enabled])
        )
      ))
      return _.orderBy(result, 'username')
    },
    highlight () {
      return this.groupDescription === '' || this.groupDescription.length < 2
    },
  },

  async mounted () {
    await this.init()
  },

  methods: {
    navToUserDetail (id) {
      if (id) {
        this.$router.push({
          name: 'hkl-user-detail',
          params: {
            id,
          },
        })
      }
    },
    userName (user) {
      if (user.username) {
        const name = userHelper.getUserName(user.username, user.displayName)
        return name
      }
    },

    handleResponse (response) {
      if (response.status >= HttpStatus.INTERNAL_SERVER_ERROR) {
        notification.fireNotification(false, this.$i18n.t('error.system'))
        return null
      }
      if (response.status >= HttpStatus.BAD_REQUEST) {
        notification.fireNotification(false, response.data)
        return null
      }
      return response.data.data
    },
    handleServerError (error) {
      const response = error.response
      this.handleResponse(response)
    },

    async init () {
      this.roleId = this.$router.currentRoute.params.id
      try {
        const response = await HTTP.get(`/roles/${this.roleId}`)
        this.groupDetail = this.handleResponse(response)
        this.groupDescription = this.groupDetail.note
        const usersResp = await HTTP.get(`/roles/${this.roleId}/users/`)
        this.userItems = this.handleResponse(usersResp) || []
      } catch (err) {
        this.handleServerError(err)
      }
    },

    async updateGroupDescription () {
      this.allowChangeDescription = false
      if (!this.groupDescription || this.groupDescription.length < 2 ||
        this.groupDescription === this.groupDetail.note) {
        this.groupDescription = this.groupDetail.note
        return
      }
      try {
        await HTTP.put(`/roles/${this.roleId}`, { name: this.groupDetail.name, note: this.groupDescription })
        this.groupDetail.note = this.groupDescription
      } catch (err) {
        this.handleServerError(err)
        this.groupDescription = this.groupDetail.note
      }
    },

    dblClickEdit () {
      if (this.aclHelper.getSettingsUpdateAccess()) {
        this.allowChangeDescription = true
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/settings/style';

.settings-engineers-security-group-detail {

  .nav-back {
    padding-left: 0;
  }

  pre {
    display: inline-block;
    vertical-align: top;
  }
  .settings-engineers-security-group-detail-name {
    width: 300px;
  }

  .detail {
    margin: 20px 0;
  }

  .status {
    padding: 0 8px;
  }
  .align-center {
    margin: auto
  }

  .nav-back, .detail, .users {
    padding: 0 24px;
  }
}
</style>
